import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, Euro, CheckCircle2, Edit, Copy, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useAlert } from './AlertProvider';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

interface GammeConfig {
    id: number;
    gamme: string;
    marque: string;
    unit: string;
    batch_size: number;
    prix_catalogue_ht: number;
    prix_vente_ttc: number;
    tva_percentage: number;
    pricing_tiers: GammePricingTier[];
}

interface GammePricingTier {
    id?: number;
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
    payment_mode?: string | null;
    payment_term?: string | null;
}

interface BrandCombinationResponse {
    gamme: string;
    unit: string;
    product_count: number;
}

interface RemovedConfigResponse {
    id: number;
    gamme: string;
    unit: string;
}

interface SyncBrandCombinationsResponse {
    removed_configs_count: number;
    removed_configs: RemovedConfigResponse[];
    remaining_combinations_count: number;
    combinations: BrandCombinationResponse[];
}

interface BrandPricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    brandName: string;
}

interface CombinationConfig {
    gamme: string;
    unit: string;
    productCount: number;
    configId: number | null;
    tierCount: number;
    tierMode: 'ug' | 'percent' | null;
    tiers: GammePricingTier[];
}

const buildCombinationKey = (gamme: string, unit: string): string => {
    return `${gamme.trim().toUpperCase()}::${unit.trim().toLowerCase()}`;
};

const normalizeBrandName = (value: string): string => {
    return value.trim().toUpperCase().replace(/\s+/g, '').replace(/_/g, '').replace(/-/g, '');
};

const BrandPricingModal: React.FC<BrandPricingModalProps> = ({
    isOpen,
    onClose,
    brandName
}) => {
    const { showSuccess, showError } = useAlert();
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<'list' | 'edit'>('list');

    // Available options
    const [availableCombinations, setAvailableCombinations] = useState<BrandCombinationResponse[]>([]);
    const [combinations, setCombinations] = useState<CombinationConfig[]>([]);

    // Edit mode state
    const [editingCombination, setEditingCombination] = useState<CombinationConfig | null>(null);
    const [tiers, setTiers] = useState<GammePricingTier[]>([]);
    const [tierMode, setTierMode] = useState<'ug' | 'percent'>('percent');
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: 'delete' | 'applyAll' | null }>({ open: false, type: null });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && brandName) {
            loadAvailableOptions();
        }
    }, [isOpen, brandName]);

    useEffect(() => {
        if (availableCombinations.length > 0) {
            loadAllCombinations();
            return;
        }

        setCombinations([]);
    }, [availableCombinations]);

    const loadAvailableOptions = async () => {
        try {
            const response = await api.get<BrandCombinationResponse[]>('/gamme-config/brand-combinations', {
                params: { marque: brandName }
            });
            const nextCombinations: BrandCombinationResponse[] = Array.isArray(response.data)
                ? response.data
                : [];

            setAvailableCombinations(nextCombinations);
        } catch (error) {
            console.error('Error loading options:', error);
            setAvailableCombinations([]);
        }
    };

    const loadAllCombinations = async () => {
        try {
            setLoading(true);
            const response = await api.get<GammeConfig[]>('/gamme-config/', {
                params: { marque: brandName }
            });
            const allConfigs: GammeConfig[] = Array.isArray(response.data) ? response.data : [];
            const configByKey: Map<string, GammeConfig> = new Map(
                allConfigs
                    .filter((config: GammeConfig) => normalizeBrandName(config.marque) === normalizeBrandName(brandName))
                    .map((config: GammeConfig) => [buildCombinationKey(config.gamme, config.unit), config])
            );

            const combos: CombinationConfig[] = availableCombinations.map((combination: BrandCombinationResponse) => {
                const config: GammeConfig | undefined = configByKey.get(
                    buildCombinationKey(combination.gamme, combination.unit)
                );

                if (config) {
                    const firstTier: GammePricingTier | undefined = config.pricing_tiers[0];
                    const mode: 'ug' | 'percent' = firstTier?.free_units !== null && firstTier?.free_units !== undefined ? 'ug' : 'percent';
                    return {
                        gamme: combination.gamme,
                        unit: combination.unit,
                        productCount: combination.product_count,
                        configId: config.id,
                        tierCount: config.pricing_tiers.length,
                        tierMode: mode,
                        tiers: config.pricing_tiers
                    };
                }

                return {
                    gamme: combination.gamme,
                    unit: combination.unit,
                    productCount: combination.product_count,
                    configId: null,
                    tierCount: 0,
                    tierMode: null,
                    tiers: []
                };
            });

            setCombinations(combos);
        } catch (error) {
            console.error('Error loading combinations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshConfigurations = async (): Promise<void> => {
        try {
            setLoading(true);
            const response = await api.post<SyncBrandCombinationsResponse>('/gamme-config/brand-combinations/sync', null, {
                params: { marque: brandName }
            });
            const payload: SyncBrandCombinationsResponse = response.data;

            setAvailableCombinations(payload.combinations || []);

            if (payload.removed_configs_count > 0) {
                showSuccess(`${payload.removed_configs_count} configuration${payload.removed_configs_count > 1 ? 's' : ''} obsolète${payload.removed_configs_count > 1 ? 's' : ''} supprimée${payload.removed_configs_count > 1 ? 's' : ''}`);
                return;
            }

            showSuccess('Configuration réalignée avec les produits existants');
        } catch (error: unknown) {
            console.error('Error syncing brand combinations:', error);
            showError('Erreur lors de la synchronisation des combinaisons');
        } finally {
            setLoading(false);
        }
    };

    const addTier = () => {
        const newTierNumber = tiers.length + 1;
        const lastTier = tiers[tiers.length - 1];
        const newMinQty = lastTier ? lastTier.min_quantity + 10 : 10;

        setTiers([...tiers, {
            tier_number: newTierNumber,
            min_quantity: newMinQty,
            free_units: null,
            discount_percentage: (lastTier?.discount_percentage || 0) + 5,
            payment_mode: null,
            payment_term: null
        }]);
    };

    const removeTier = (index: number) => {
        const newTiers = tiers.filter((_, i) => i !== index);
        newTiers.forEach((tier, i) => {
            tier.tier_number = i + 1;
        });
        setTiers(newTiers);
    };

    const updateTier = (index: number, field: keyof GammePricingTier, value: any) => {
        const newTiers = [...tiers];
        newTiers[index] = { ...newTiers[index], [field]: value };
        setTiers(newTiers);
    };

    const handleModeChange = (mode: 'ug' | 'percent') => {
        setTierMode(mode);
        setTiers([]);
    };

    const saveCombinationConfig = async (combination: CombinationConfig, tiersToSave: GammePricingTier[], mode: 'ug' | 'percent') => {
        try {
            let configId = combination.configId;

            // If no configId, check if config exists in backend
            if (!configId) {
                const checkResponse = await api.get<GammeConfig[]>('/gamme-config/', {
                    params: { marque: brandName }
                });
                const existingConfigs: GammeConfig[] = Array.isArray(checkResponse.data) ? checkResponse.data : [];
                const existingConfig = existingConfigs.find((config: GammeConfig) =>
                    buildCombinationKey(config.gamme, config.unit) === buildCombinationKey(combination.gamme, combination.unit) &&
                    normalizeBrandName(config.marque) === normalizeBrandName(brandName)
                );
                if (existingConfig) {
                    configId = existingConfig.id;
                }
            }

            // Create or update GammeConfig
            if (configId) {
                await api.put(`/gamme-config/${configId}`, {
                    gamme: combination.gamme,
                    marque: brandName,
                    unit: combination.unit,
                    batch_size: 1,
                    prix_catalogue_ht: 0,
                    prix_vente_ttc: 0,
                    tva_percentage: 20.0
                });
            } else {
                const response = await api.post('/gamme-config/', {
                    gamme: combination.gamme,
                    marque: brandName,
                    unit: combination.unit,
                    batch_size: 1,
                    prix_catalogue_ht: 0,
                    prix_vente_ttc: 0,
                    tva_percentage: 20.0
                });
                configId = response.data.id;
            }

            // Delete existing tiers
            const existingTiersResponse = await api.get(`/gamme-config/${configId}/tiers`);
            const existingTiers = existingTiersResponse.data;
            for (const tier of existingTiers) {
                await api.delete(`/gamme-config/${configId}/tiers/${tier.id}`);
            }

            // Create new tiers
            for (const tier of tiersToSave) {
                await api.post(`/gamme-config/${configId}/tiers`, {
                    tier_number: tier.tier_number,
                    min_quantity: tier.min_quantity,
                    max_quantity: null,
                    discount_percentage: mode === 'percent' ? tier.discount_percentage : null,
                    free_units: mode === 'ug' ? tier.free_units : null,
                    payment_mode: tier.payment_mode,
                    payment_term: tier.payment_term
                });
            }

            // Auto-propagate tiers to all existing BPUs
            await api.post(`/gamme-config/${configId}/propagate-tiers`);
        } catch (error) {
            throw error;
        }
    };

    const handleSave = async () => {
        if (!editingCombination || tiers.length === 0) {
            showError('Veuillez ajouter au moins un palier');
            return;
        }

        setLoading(true);
        try {
            await saveCombinationConfig(editingCombination, tiers, tierMode);
            showSuccess('Configuration enregistrée avec succès');
            await loadAllCombinations();
            setView('list');
            setEditingCombination(null);
            setTiers([]);
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyToAll = () => {
        if (!editingCombination || tiers.length === 0) {
            showError('Veuillez configurer au moins un palier');
            return;
        }
        setDeleteDialog({ open: true, type: 'applyAll' });
    };

    const handleConfirmApplyToAll = async () => {

        setIsProcessing(true);
        try {
            let successCount = 0;
            for (const combo of combinations) {
                try {
                    await saveCombinationConfig(combo, tiers, tierMode);
                    successCount++;
                } catch (error) {
                    console.error(`Error saving config for ${combo.gamme} + ${combo.unit}:`, error);
                }
            }
            showSuccess(`Configuration appliquée à ${successCount}/${combinations.length} combinaisons`);
            setDeleteDialog({ open: false, type: null });
            await loadAllCombinations();
            setView('list');
            setEditingCombination(null);
            setTiers([]);
        } catch (error: any) {
            showError('Erreur lors de l\'application en masse');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = () => {
        if (!editingCombination?.configId) return;
        setDeleteDialog({ open: true, type: 'delete' });
    };

    const handleConfirmDelete = async () => {
        if (!editingCombination?.configId) return;

        setIsProcessing(true);
        try {
            await api.delete(`/gamme-config/${editingCombination.configId}`);
            showSuccess('Configuration supprimée');
            setDeleteDialog({ open: false, type: null });
            await loadAllCombinations();
            setView('list');
            setEditingCombination(null);
            setTiers([]);
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEditCombination = (combination: CombinationConfig) => {
        setEditingCombination(combination);
        if (combination.tierMode === 'ug') {
            setTiers([]);
        } else {
            setTiers(combination.tiers);
        }
        setTierMode('percent');
        setView('edit');
    };

    const handleBackToList = () => {
        setView('list');
        setEditingCombination(null);
        setTiers([]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:p-6 border-b border-slate-200 dark:border-slate-700">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base sm:text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <Euro className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 shrink-0" />
                            <span>Configuration des Paliers - {brandName}</span>
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                            {view === 'list'
                                ? `${combinations.length} combinaisons produits existants`
                                : `${editingCombination?.gamme} + ${editingCombination?.unit}`
                            }
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                    {view === 'list' ? (
                        /* LIST VIEW - Show all combinations */
                        <div className="space-y-3">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : combinations.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Aucune combinaison disponible</p>
                                    <p className="text-sm mt-1">Ajoutez des gammes et unités dans le configurateur</p>
                                </div>
                            ) : (
                                combinations.map((combo) => (
                                    <div
                                        key={`${combo.gamme}-${combo.unit}`}
                                        className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 sm:p-4 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
                                    >
                                        <div className="flex items-start sm:items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                {combo.tierCount > 0 ? (
                                                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                                )}
                                                <div>
                                                    <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                                                        {combo.gamme} + {combo.unit}
                                                    </h3>
                                                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                                        {combo.tierCount > 0
                                                            ? `${combo.tierCount} palier${combo.tierCount > 1 ? 's' : ''} configuré${combo.tierCount > 1 ? 's' : ''}`
                                                            : 'Aucun palier configuré'
                                                        }
                                                    </p>
                                                    <p className="text-[11px] sm:text-xs text-slate-400 dark:text-slate-500 mt-1">
                                                        {combo.productCount} produit{combo.productCount > 1 ? 's' : ''} existant{combo.productCount > 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleEditCombination(combo)}
                                                className="shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm ml-auto"
                                            >
                                                <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                {combo.tierCount > 0 ? 'Modifier' : 'Configurer'}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        /* EDIT VIEW - Configure tiers for selected combination */
                        <div className="space-y-6">
                            {/* Info box */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4 flex gap-2 sm:gap-3">
                                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                                    <p className="font-medium mb-1">Application automatique</p>
                                    <p>Les paliers seront appliqués à tous les produits : {brandName} + {editingCombination?.gamme} + {editingCombination?.unit}</p>
                                </div>
                            </div>

                            {/* Tier Mode Selection */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Type de palier
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleModeChange('percent')}
                                        className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${tierMode === 'percent'
                                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                            : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
                                            }`}
                                    >
                                        <div className="font-medium">Remise en %</div>
                                    </button>
                                </div>
                            </div>

                            {/* Tiers Configuration */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Paliers de tarification
                                    </label>
                                    <button
                                        onClick={addTier}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Ajouter un palier
                                    </button>
                                </div>

                                {tiers.length === 0 ? (
                                    <div className="text-center py-8 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
                                        <p className="text-sm">Aucun palier configuré</p>
                                        <p className="text-xs mt-1">Cliquez sur "Ajouter un palier" pour commencer</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {tiers.map((tier, index) => (
                                            <div
                                                key={index}
                                                className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50"
                                            >
                                                <div className="flex items-start gap-2 sm:gap-4">
                                                    <div className="flex-shrink-0 w-12 sm:w-20">
                                                        <label className="block text-[10px] sm:text-xs text-slate-500 mb-1">Palier</label>
                                                        <div className="text-base sm:text-lg font-semibold text-orange-600">#{tier.tier_number}</div>
                                                    </div>

                                                    <div className="flex-1 grid grid-cols-2 gap-2 sm:gap-4">
                                                        <div>
                                                            <label className="block text-[10px] sm:text-xs text-slate-500 mb-1">
                                                                Qté min
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={tier.min_quantity}
                                                                onChange={(e) => updateTier(index, 'min_quantity', parseInt(e.target.value) || 0)}
                                                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                                min="1"
                                                                step="1"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-[10px] sm:text-xs text-slate-500 mb-1">
                                                                Remise (%)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={tier.discount_percentage || 0}
                                                                onChange={(e) => {
                                                                    updateTier(index, 'discount_percentage', parseFloat(e.target.value) || 0);
                                                                }}
                                                                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
                                                                min="0"
                                                                step={0.5}
                                                            />
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => removeTier(index)}
                                                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Supprimer ce palier"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-3 sm:p-6 border-t border-slate-200 dark:border-slate-700 gap-2">
                    {view === 'list' ? (
                        /* List view footer */
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-3">
                                <div className="text-sm text-slate-500">
                                    {combinations.filter(c => c.tierCount > 0).length} / {combinations.length} configurées
                                </div>
                                <button
                                    onClick={handleRefreshConfigurations}
                                    disabled={loading}
                                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </button>
                            </div>
                            <button
                                onClick={onClose}
                                className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    ) : (
                        /* Edit view footer */
                        <>
                            <div className="flex items-center gap-1.5 sm:gap-3">
                                <button
                                    onClick={handleBackToList}
                                    disabled={loading}
                                    className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    ← Retour
                                </button>
                                {editingCombination?.configId && (
                                    <button
                                        onClick={handleDelete}
                                        disabled={loading}
                                        className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                                        <span className="hidden sm:inline">Supprimer</span>
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 sm:gap-3">
                                {tiers.length > 0 && (
                                    <button
                                        onClick={handleApplyToAll}
                                        disabled={loading}
                                        className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2"
                                    >
                                        <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Appliquer à toutes</span>
                                        <span className="sm:hidden">Appliquer</span>
                                    </button>
                                )}
                                <button
                                    onClick={handleSave}
                                    disabled={loading || tiers.length === 0}
                                    className="px-3 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Enregistrement...
                                        </>
                                    ) : (
                                        'Enregistrer'
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog({ open, type: open ? deleteDialog.type : null })}
                title={deleteDialog.type === 'delete' ? 'Supprimer la configuration' : 'Appliquer à toutes les combinaisons'}
                description={
                    deleteDialog.type === 'delete'
                        ? 'Êtes-vous sûr de vouloir supprimer cette configuration ? Cette action est irréversible.'
                        : `Appliquer cette configuration (${tiers.length} paliers) à toutes les ${combinations.length} combinaisons ?`
                }
                confirmText={deleteDialog.type === 'delete' ? 'Supprimer' : 'Appliquer'}
                cancelText="Annuler"
                isLoading={isProcessing}
                onConfirm={deleteDialog.type === 'delete' ? handleConfirmDelete : handleConfirmApplyToAll}
            />
        </div>
    );
};

export default BrandPricingModal;
